// kan-111 AI Reference Taker — Settings → AI References tab (tab id "9").
// Executive/Admin always get the all-users view (allAiReferences) with a
// "Requested By" select to narrow it; everyone else sees only their own
// requests (myAiReferences). No toggle — user decision 2026-10-12.
// Layout follows _design/design_handoff_ai_references_table_2a and reuses the
// candidate References tab building blocks (ReferenceTableParts / .refTabs).
// Visibility of the tab is gated in Settings.tsx; the content itself is not
// (My Earning precedent, so executives can preview while impersonating).
import { useLazyQuery, useQuery } from '@apollo/client';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import {
  Button,
  Collapse,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import CLOSE_ICON from '../../../assets/images/close-icon.svg';
import Loader from '../../../components/layout/Loader';
import { customSelectStyleNew } from '../../../components/styled/customSelect';
import { AppContext } from '../../../context';
import { ROLES, VIEW_CANDIDATE_ROUTE } from '../../../utils/constant';
import { FETCHING_USERS_MINIMIZEVERSION } from '../../admin/gql';
import {
  REF_PAGE_SIZES,
  RefBadge,
  RefDateCell,
  RefEmptyRow,
  RefFooter,
  RefSectionHeader,
  formatRefDate,
} from '../../candidate-pipeline/reference/ReferenceTableParts';
import {
  ALL_AI_REFERENCES,
  AiReferenceSettingsFilter,
  AiReferenceSettingsResult,
  AiReferenceSettingsRow,
  MY_AI_REFERENCES,
  SETTINGS_AI_REFERENCE_ANSWERS,
} from './gql';
import {
  AI_REFERENCE_STATUS_META,
  AI_REFERENCE_STATUS_OPTIONS,
  ANSWERABLE_STATUSES,
  lastActivity,
  personName,
  statusDetail,
  statusOptionKeys,
} from './utils';
import './AiReferences.scss';
import AiReferenceTrailModal from './AiReferenceTrailModal';

let searchTimer = 0;
const SEARCH_DEBOUNCE_MS = 300;

// Default range = the current calendar year (same as My Earning).
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_FROM_DATE = `${CURRENT_YEAR}-01-01`;
const DEFAULT_TO_DATE = `${CURRENT_YEAR}-12-31`;

// Candidate detail, References tab (tab 6).
const REFERENCES_TAB = '6';

// Title now sits under the reference name (handoff 2a), so no Title column.
const TABLE_HEADER = [
  'Candidate',
  'Reference',
  'Requested By',
  'Status',
  'Last Activity',
  'Completed',
  'Action',
];

type SelectOption = { label: string; value: string };

// ---------------------------------------------------------------------------
// View answers modal — a minimal local copy of the candidate section's modal
// (lane C's AiReferenceAnswersModal is not exported).
// ---------------------------------------------------------------------------

const AnswersModal = ({
  row,
  toggle,
}: {
  row: AiReferenceSettingsRow | null;
  toggle: () => void;
}) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const [getAnswers, { data, loading, error }] = useLazyQuery(
    SETTINGS_AI_REFERENCE_ANSWERS,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (row?.entryId) {
      setShowTranscript(false);
      getAnswers({ variables: { referenceEntryId: row.entryId } });
    }
    // eslint-disable-next-line
  }, [row?.entryId]);

  const result = data?.aiReferenceAnswers;
  const answers = [...(result?.answers ?? [])].sort(
    (a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0)
  );

  return (
    <Modal
      isOpen={!!row}
      toggle={toggle}
      className="revampDialog revampDialogWidth add-reference-dialog"
      scrollable
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <h5 className="m-0">
          AI Reference — {row?.referenceName ?? 'Reference'}
        </h5>
        <Button className="p-0 bg-transparent border-0" onClick={toggle}>
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>

      <ModalBody className="modalBodyScroll">
        {loading && <Loader loaderClass="sm-height" />}
        {!loading && error && (
          <p className="text-danger">Could not load answers: {error.message}</p>
        )}
        {!loading && result && (
          <>
            <div className="mb-3">
              <h6>Answers</h6>
              {answers.length ? (
                answers.map((a: any) => (
                  <div key={a.sortOrder} className="mb-3">
                    <div>
                      <strong>{a.label}</strong>
                    </div>
                    {a.prompt && (
                      <small className="text-muted d-block mb-1">{a.prompt}</small>
                    )}
                    <p className="rich-text-paragraph mb-0">{a.answer || '--'}</p>
                  </div>
                ))
              ) : (
                <p className="text-muted mb-0">No answers recorded yet.</p>
              )}
            </div>

            {result.recordingUrl && (
              <div className="mb-3">
                <h6>Recording</h6>
                <a href={result.recordingUrl} target="_blank" rel="noopener noreferrer">
                  Listen to the call recording
                </a>
              </div>
            )}

            {result.transcript && (
              <div>
                <Button
                  className="border-0 bg-transparent color-default p-0"
                  size="sm"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  {showTranscript ? 'Hide transcript' : 'Show transcript'}
                </Button>
                <Collapse isOpen={showTranscript}>
                  <pre
                    className="mt-2 p-2 border rounded"
                    style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}
                  >
                    {result.transcript}
                  </pre>
                </Collapse>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter className="pt-3">
        <button type="button" onClick={toggle} className="buttonPrimary">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

const AiReferences = () => {
  const { userRoles, theme } = useContext(AppContext);
  const canViewAllUsers: boolean =
    !!userRoles?.includes(ROLES.EXECUTIVE) || !!userRoles?.includes(ROLES.ADMIN);
  const showingAllUsers = canViewAllUsers;

  const [requestedBy, setRequestedBy] = useState<SelectOption | null>(null);
  const [statusOption, setStatusOption] = useState<SelectOption | null>(null);
  const [fromDate, setFromDate] = useState<string>(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState<string>(DEFAULT_TO_DATE);
  const [searchText, setSearchText] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(REF_PAGE_SIZES[0]);

  const [answersRow, setAnswersRow] = useState<AiReferenceSettingsRow | null>(null);
  const [trailRow, setTrailRow] = useState<AiReferenceSettingsRow | null>(null);

  const [getMine, mine] = useLazyQuery<{ myAiReferences: AiReferenceSettingsResult }>(
    MY_AI_REFERENCES,
    { fetchPolicy: 'network-only' }
  );
  const [getAll, all] = useLazyQuery<{ allAiReferences: AiReferenceSettingsResult }>(
    ALL_AI_REFERENCES,
    { fetchPolicy: 'network-only' }
  );

  const { data: usersData } = useQuery(FETCHING_USERS_MINIMIZEVERSION, {
    skip: !showingAllUsers,
    fetchPolicy: 'cache-first',
  });
  const userOptions: SelectOption[] = (usersData?.allUsersDropDown ?? []).map(
    (item: any) => ({ label: item?.fullName, value: item?.id })
  );


  const fetchList = useCallback(() => {
    const filters: AiReferenceSettingsFilter = {};
    const statuses = statusOptionKeys(statusOption?.value);
    if (statuses.length) {
      filters.statuses = statuses;
    }
    if (fromDate) {
      filters.dateFrom = fromDate;
    }
    if (toDate) {
      filters.dateTo = toDate;
    }
    if (search.trim()) {
      filters.search = search.trim();
    }
    if (showingAllUsers && requestedBy?.value) {
      filters.userIds = [requestedBy.value];
    }

    const variables = { filters, page: currentPage, limit };
    if (showingAllUsers) {
      getAll({ variables });
    } else {
      getMine({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showingAllUsers,
    requestedBy,
    statusOption,
    fromDate,
    toDate,
    search,
    currentPage,
    limit,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // The tab unmounts when the user switches away — drop any pending debounce.
  useEffect(() => {
    return () => {
      window.clearTimeout(searchTimer);
    };
  }, []);

  const onSearchChange = (value: string) => {
    setSearchText(value);
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      setCurrentPage(1);
      setSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const filtersAtDefault =
    !requestedBy &&
    !statusOption &&
    fromDate === DEFAULT_FROM_DATE &&
    toDate === DEFAULT_TO_DATE &&
    !searchText &&
    !search;

  const clearFilters = () => {
    window.clearTimeout(searchTimer);
    setRequestedBy(null);
    setStatusOption(null);
    setFromDate(DEFAULT_FROM_DATE);
    setToDate(DEFAULT_TO_DATE);
    setSearchText('');
    setSearch('');
    setCurrentPage(1);
  };

  const active = showingAllUsers ? all : mine;
  const result = showingAllUsers ? all.data?.allAiReferences : mine.data?.myAiReferences;
  const rows: AiReferenceSettingsRow[] = result?.rows ?? [];
  const count = result?.count ?? 0;
  const loading = active.loading;

  // A narrower filter can leave us past the last page.
  useEffect(() => {
    if (!count) return;
    const lastPage = Math.max(1, Math.ceil(count / limit));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [count, limit, currentPage]);

  // Shared select styles (keeps the dark-mode colours), resized so the
  // dropdowns match the 38px / 4px-radius / 14px inputs in the same row
  // (handoff 2a: one control height across the filter row).
  const baseSelectStyles: any = customSelectStyleNew(theme);
  const selectStyles: any = {
    ...baseSelectStyles,
    control: (styles: any, state: any) => ({
      ...baseSelectStyles.control(styles, state),
      minHeight: 38,
      height: 38,
      padding: 0,
      borderRadius: 4,
      fontSize: 14,
      boxShadow: 'none',
      ...(theme === 'light' ? { borderColor: state.isFocused ? '#eaa827' : '#cbd0d8' } : {}),
      '&:hover': {
        borderColor: theme === 'light' ? (state.isFocused ? '#eaa827' : '#cbd0d8') : '#525252',
      },
    }),
    valueContainer: (provided: any) => ({ ...provided, padding: '0 12px', height: 36 }),
    indicatorsContainer: (provided: any) => ({ ...provided, height: 36 }),
    placeholder: (provided: any) => ({ ...provided, color: '#9aa5b1' }),
    container: (provided: any) => ({ ...provided, fontSize: 14 }),
  };

  return (
    <div className="admin-tabs team-tabs ai-references-wrap">
      <div className={`refTabs aiRefPage${showingAllUsers ? '' : ' aiRefPageSelf'}`}>
        <RefSectionHeader
          title="AI References"
          count={count}
          description={
            showingAllUsers
              ? 'AI reference requests across all candidates'
              : 'Your AI reference requests'
          }
        />

        <div className="aiRefFilters">
          <div className="aiRefFilterField">
            <Label for="aiReferenceSearch">Search</Label>
            <Input
              id="aiReferenceSearch"
              placeholder="Candidate or reference name"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {showingAllUsers && (
            <div className="aiRefFilterField">
              <Label>Requested By</Label>
              <Select
                isSearchable
                isClearable
                placeholder="All users"
                options={userOptions}
                value={requestedBy}
                onChange={(value: any) => {
                  setCurrentPage(1);
                  setRequestedBy((value as SelectOption) ?? null);
                }}
                styles={selectStyles}
              />
            </div>
          )}

          <div className="aiRefFilterField">
            <Label>Status</Label>
            <Select
              isClearable
              placeholder="All statuses"
              options={AI_REFERENCE_STATUS_OPTIONS}
              value={statusOption}
              onChange={(value: any) => {
                setCurrentPage(1);
                setStatusOption((value as SelectOption) ?? null);
              }}
              styles={selectStyles}
            />
          </div>

          <div className="aiRefFilterField">
            <Label for="aiReferenceFromDate">Created From</Label>
            <Input
              id="aiReferenceFromDate"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setCurrentPage(1);
                setFromDate(e.target.value);
              }}
            />
          </div>

          <div className="aiRefFilterField">
            <Label for="aiReferenceToDate">Created To</Label>
            <Input
              id="aiReferenceToDate"
              type="date"
              value={toDate}
              onChange={(e) => {
                setCurrentPage(1);
                setToDate(e.target.value);
              }}
            />
          </div>

          <div className="aiRefClearCell">
            <button
              type="button"
              className="refBtn refBtnGhost"
              onClick={clearFilters}
              disabled={filtersAtDefault}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="refTableScroll">
          <div className="refTable aiRefTable">
            <div role="table" aria-label="AI references">
              <div className="refGridRow refHeadRow aiRefGrid" role="row">
                {TABLE_HEADER.map((h) => (
                  <span key={h} role="columnheader">
                    {h}
                  </span>
                ))}
              </div>

              {loading && !rows.length && (
                <div className="refLoadingRow">
                  <Loader loaderClass="sm-height " />
                </div>
              )}

              {!loading && !rows.length && (
                <RefEmptyRow text="No AI references match these filters" />
              )}

              {rows.map((row) => {
                const meta = AI_REFERENCE_STATUS_META[row.status];
                const detail = statusDetail(row);
                const last = lastActivity(row);
                return (
                  <div
                    key={row.entryId}
                    className="refGridRow refBodyRow aiRefGrid"
                    role="row"
                  >
                    <div role="cell">
                      {row.candidate?.id ? (
                        <Link
                          className="aiRefCandidateLink"
                          title={personName(row.candidate)}
                          to={{
                            pathname: `${VIEW_CANDIDATE_ROUTE}/${row.candidate.id}`,
                            search: `?activeTab=${REFERENCES_TAB}`,
                          }}
                        >
                          {personName(row.candidate)}
                        </Link>
                      ) : (
                        <span className="refDash">--</span>
                      )}
                    </div>

                    <div role="cell" className="refCellStack">
                      <span className="refName" title={row.referenceName ?? undefined}>
                        {row.referenceName || '--'}
                      </span>
                      {row.title && (
                        <span className="refMeta" title={row.title}>
                          {row.title}
                        </span>
                      )}
                      {row.email && (
                        <span className="refEmail aiRefEmailSmall" title={row.email}>
                          {row.email}
                        </span>
                      )}
                    </div>

                    <div role="cell" className="aiRefEllipsis">
                      {personName(row.requestedBy)}
                    </div>

                    <div role="cell">
                      <RefBadge tone={meta?.tone ?? 'neutral'} detail={detail}>
                        {meta?.label ?? row.status}
                      </RefBadge>
                    </div>

                    <div role="cell">
                      <RefDateCell value={last} />
                    </div>

                    <div role="cell" className="aiRefMuted">
                      {formatRefDate(row.completedAt) ?? <span className="refDash">--</span>}
                    </div>

                    <div role="cell" className="refActionCell aiRefActions">
                      {ANSWERABLE_STATUSES.includes(row.status) && (
                        <button
                          type="button"
                          className="refBtn refBtnNeutralOutline"
                          onClick={() => setAnswersRow(row)}
                        >
                          View Answers
                        </button>
                      )}
                      <button
                        type="button"
                        className="refBtn refBtnNeutralOutline"
                        onClick={() => setTrailRow(row)}
                      >
                        View Trail
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <RefFooter
              page={currentPage}
              pageSize={limit}
              total={count}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setCurrentPage(1);
                setLimit(size);
              }}
            />
          </div>
        </div>
      </div>

      <AnswersModal row={answersRow} toggle={() => setAnswersRow(null)} />
      <AiReferenceTrailModal row={trailRow} toggle={() => setTrailRow(null)} />
    </div>
  );
};

export default AiReferences;
